import { PriceUtils } from '@wix/bookings-uou-mappers';
import {
  useEnvironment,
  useExperiments,
  useTranslation,
} from '@wix/yoshi-flow-editor';
import React from 'react';
import { ExperimentsConsts } from '../../../../../consts/experiments';
import { FormNestedSlot } from '../../../../../types/formSelectedSlots';
import { PaymentType } from '../../../../../types/types';
import {
  SelectedProduct,
  SlotService,
  V3Product,
} from '../../../../../utils/state/types';
import { TagName } from '../../../../../utils/tagName.const';
import Text, { TextType } from '../../../Text/Text';
import { useFormComponentContext } from '../../../Hooks/useFormComponentContext';
import DateAndTime from '../../DateAndTime/DateAndTime';
import VideoConferenceBadge from '../../VideoConferenceBadge/VideoConferenceBadge';
import { SingleSlotDetailsDataHook } from './dataHooks';
import { MoreDetails } from './MoreDetails';
import PriceLine from './PriceLine/PriceLine';
import { SelectedAddons } from './SelectedAddons';
import { SelectedRelatedProducts } from './SelectedRelatedProducts';
import { classes } from './SingleSlotDetails.st.css';

export type SingleSlotDetailsProps = {
  slotService: SlotService;
  slot: FormNestedSlot;
  totalNumberOfParticipants?: number;
  isSingleService: boolean;
  isCart: boolean | undefined;
  selectedProductAddons?: SelectedProduct[];
  relatedProducts?: V3Product[];
};

const SingleSlotDetails: React.FC<SingleSlotDetailsProps> = ({
  slotService,
  slot,
  totalNumberOfParticipants,
  isSingleService,
  isCart,
  selectedProductAddons,
  relatedProducts,
}) => {
  const { isMobile } = useEnvironment();
  const { experiments } = useExperiments();
  const { t } = useTranslation();
  const isShowSlotMoreDetailsOnMobileEnabled = experiments.enabled(
    ExperimentsConsts.ShowSlotMoreDetailsOnMobile,
  );
  const { dateRegionalSettingsLocale } = useFormComponentContext();
  const { service, selectedPaymentOption, paymentDetails } = slotService;
  const isSubscription =
    selectedPaymentOption?.type === PaymentType.SUBSCRIPTION;
  const dynamicPriceTotalNumberOfParticipants =
    slotService.dynamicPriceInfo?.selectedVariants?.reduce(
      (acc, next) => (acc += next.numberOfParticipants || 0),
      0,
    );

  const renderMoreDetailsSection = () => {
    // On mobile, when enabled, show the same details as desktop but always
    // visible (no expand/collapse accordion). Otherwise mobile keeps showing
    // only addons and related products.
    if (!isMobile || isShowSlotMoreDetailsOnMobileEnabled) {
      return (
        <MoreDetails
          slotService={slotService}
          slot={slot}
          isSingleService={isSingleService}
          selectedProductAddons={selectedProductAddons}
          relatedProducts={relatedProducts}
          alwaysExpanded={isMobile}
        />
      );
    }

    return (
      <>
        <SelectedAddons slotService={slotService} />
        <SelectedRelatedProducts
          selectedProductAddons={selectedProductAddons}
          relatedProducts={relatedProducts}
          paymentDetails={slotService.paymentDetails}
        />
      </>
    );
  };

  return (
    <div className={classes.root}>
      <Text
        type={TextType.Primary}
        data-hook={SingleSlotDetailsDataHook.SERVICE_NAME}
        tagName={TagName.Paragraph}
      >
        {service.name}
      </Text>
      <DateAndTime
        totalNumberOfSessions={service.totalNumberOfSessions}
        slot={slot}
        service={service}
      />
      <VideoConferenceBadge
        includesConferenceOption={service.includesConferenceOption}
      />
      {isSingleService && isSubscription && paymentDetails.price > 0 && (
        <Text
          type={TextType.Secondary}
          data-hook={SingleSlotDetailsDataHook.PRICE_LINE}
          tagName={TagName.Paragraph}
        >
          {t('app.payment.summary.subscription.monthly-price', {
            price: PriceUtils.getFormattedCurrency({
              price: paymentDetails.price,
              currency: paymentDetails.currency,
              locale: dateRegionalSettingsLocale,
            }),
          })}
        </Text>
      )}
      {renderMoreDetailsSection()}
      {!isSingleService &&
        (slotService.dynamicPriceInfo?.selectedVariants?.length ? (
          slotService.dynamicPriceInfo.selectedVariants.map((variant) => (
            <PriceLine
              key={variant?.choices?.[0].optionId}
              dataHook={SingleSlotDetailsDataHook.PRICE_LINE}
              selectedPaymentOption={selectedPaymentOption}
              slotService={slotService}
              selectedVariant={variant}
              totalNumberOfParticipants={dynamicPriceTotalNumberOfParticipants}
              isCart={isCart}
            />
          ))
        ) : (
          <PriceLine
            dataHook={SingleSlotDetailsDataHook.PRICE_LINE}
            selectedPaymentOption={selectedPaymentOption}
            slotService={slotService}
            totalNumberOfParticipants={totalNumberOfParticipants}
            isCart={isCart}
          />
        ))}
    </div>
  );
};

export default SingleSlotDetails;
